import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './AIPreferenceChat.css';
import { API_BASE_URL, fetchWithCSRF } from '../config';

function AIPreferenceChat({ studentProfile, schoolCounts, initialRecommendations, onFilterApplied, reportUuid, onProfileUpdated }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [readyToFilter, setReadyToFilter] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    // Scroll only within the chat container, not the whole page
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Start conversation automatically
  useEffect(() => {
    startConversation();
  }, []);

  const startConversation = async () => {
    setLoading(true);
    try {
      const payload = {
        conversation_history: [],
        student_profile: studentProfile,
        school_counts: schoolCounts,
        report_uuid: reportUuid
      };

      const response = await fetchWithCSRF(`${API_BASE_URL}/api/universities/ai/chat/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      setMessages([{ role: 'assistant', content: data.message }]);
      
      // If profile was updated, notify parent component
      if (data.updated_profile && onProfileUpdated) {
        onProfileUpdated(data.updated_profile);
      }
      
      // Check if AI is ready to filter
      if (data.ready_to_filter) {
        setReadyToFilter(true);
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      setMessages([{ 
        role: 'assistant', 
        content: 'Hi! I can help you narrow down your college list. What location would you prefer for college?' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const response = await fetchWithCSRF(`${API_BASE_URL}/api/universities/ai/chat/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_history: newMessages,
          student_profile: studentProfile,
          school_counts: schoolCounts,
          report_uuid: reportUuid
        })
      });

      const data = await response.json();
      setMessages([...newMessages, { role: 'assistant', content: data.message }]);

      // If profile was updated, notify parent component
      if (data.updated_profile && onProfileUpdated) {
        onProfileUpdated(data.updated_profile);
      }

      // Show filter button when AI indicates it's ready
      if (data.ready_to_filter) {
        setReadyToFilter(true);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages([...newMessages, { 
        role: 'assistant', 
        content: 'Sorry, I had trouble understanding that. Could you rephrase?' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    setLoading(true);
    try {
      const response = await fetchWithCSRF(`${API_BASE_URL}/api/universities/ai/extract-and-filter/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_history: messages,
          student_profile: studentProfile,
          recommendations: initialRecommendations
        })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const filteredRecommendations = await response.json();

      if (!filteredRecommendations.summary) {
        throw new Error('Invalid response from server');
      }

      onFilterApplied(filteredRecommendations);
    } catch (error) {
      console.error('Error applying filters:', error);
      alert('There was an error applying your preferences. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="ai-chat-container">
      <div className="ai-chat-header">
        <h3>AI College Advisor</h3>
        <p>Let me help you narrow down from {schoolCounts.total_count} schools to a manageable list</p>
        {messages.length > 1 && !readyToFilter && (
          <button
            onClick={() => setReadyToFilter(true)}
            disabled={loading}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              backgroundColor: 'transparent',
              color: '#059669',
              border: '1px solid #059669',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Done Chatting? See Recommendations →
          </button>
        )}
      </div>

      <div className="ai-chat-messages" ref={chatContainerRef}>
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.role}`}>
            <div className="message-content markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {msg.content}
              </ReactMarkdown>
            </div>
          </div>
        ))}
        {loading && (
          <div className="message assistant">
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="ai-chat-input-container">
        {!readyToFilter ? (
          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
            <input
              type="text"
              className="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your response..."
              disabled={loading}
              style={{ flex: 1 }}
            />
            <button
              className="ai-send-button"
              onClick={sendMessage}
              disabled={loading || !input.trim()}
            >
              Send
            </button>
          </div>
        ) : (
          <div style={{ width: '100%' }}>
            <button 
              className="ai-filter-button" 
              onClick={applyFilters}
              disabled={loading}
              style={{
                width: '100%',
                padding: '14px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 'bold'
              }}
            >
              {loading ? 'Updating Your School List...' : 'See Updated School List'}
            </button>
            <button 
              className="ai-continue-button"
              onClick={() => setReadyToFilter(false)}
              disabled={loading}
              style={{
                marginTop: '8px',
                width: '100%',
                padding: '10px',
                backgroundColor: 'transparent',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Continue Refining Preferences
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default AIPreferenceChat;

